import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { SERVICES, TIME_SLOTS, NOT_SURE, PHONE_DISPLAY } from "../../lib/site";

const today = () => new Date().toLocaleDateString("en-CA");

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="mb-2 block text-xs uppercase tracking-[0.2em] text-slate-300">{label}</span>
    {children}
  </label>
);

export const BookingForm = () => {
  const [params] = useSearchParams();
  const pre = SERVICES.find((s) => s.id === params.get("service"))?.title || "";
  const empty = { name: "", phone: "", email: "", date: "", time: "", service: pre, message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", form);
      setDone(data);
      toast.success("Your appointment request has been received.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div data-testid="booking-success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="border border-[#D4AF37]/40 bg-[#150C28] p-8 sm:p-12">
        <CheckCircle2 className="h-12 w-12 text-[#D4AF37]" strokeWidth={1.2} />
        <h3 className="font-display mt-6 text-4xl text-white">Thank you, {done.name?.split(" ")[0]}.</h3>
        <p className="mt-4 max-w-lg leading-relaxed text-slate-300">
          Your request for <span className="text-[#F3E5AB]">{done.service}</span> on {done.date} at {done.time} has been received.
          We will contact you shortly to confirm. For anything urgent, call {PHONE_DISPLAY}.
        </p>
        <button data-testid="booking-new-request" onClick={() => { setDone(null); setForm(empty); }}
          className="mt-8 border-b border-[#D4AF37]/50 pb-1 text-sm text-[#F3E5AB] hover:border-[#D4AF37]">
          Make another request
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="booking-form" className="grid gap-5 sm:grid-cols-2">
      <Field label="Full name"><input required minLength={2} className="field" value={form.name} onChange={set("name")} placeholder="Your name" data-testid="booking-name-input" /></Field>
      <Field label="Phone number"><input required type="tel" minLength={7} className="field" value={form.phone} onChange={set("phone")} placeholder="+1 ___ ___ ____" data-testid="booking-phone-input" /></Field>
      <Field label="Email" className="sm:col-span-2"><input required type="email" className="field" value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="booking-email-input" /></Field>
      <Field label="Preferred date"><input required type="date" min={today()} className="field" value={form.date} onChange={set("date")} data-testid="booking-date-input" /></Field>
      <Field label="Preferred time">
        <select required className="field" value={form.time} onChange={set("time")} data-testid="booking-time-select">
          <option value="" disabled>Select a time</option>
          {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Service required" className="sm:col-span-2">
        <select required className="field" value={form.service} onChange={set("service")} data-testid="booking-service-select">
          <option value="" disabled>Choose a service</option>
          {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
          <option value={NOT_SURE}>Not sure yet — please advise</option>
        </select>
      </Field>
      <Field label="Message (optional)" className="sm:col-span-2">
        <textarea rows={5} maxLength={2000} className="field resize-none" value={form.message} onChange={set("message")} placeholder="Share anything you'd like us to know before your session" data-testid="booking-message-input" />
      </Field>
      <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-400">Your details stay private and are used only to arrange your session.</p>
        <button type="submit" disabled={loading} data-testid="booking-submit-button"
          className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-9 font-medium text-[#07040D] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#F3E5AB] disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Sending…" : "Book Appointment"}
        </button>
      </div>
    </form>
  );
};
